// ABOUTME: Offline-first sync service for bidirectional cloud synchronization
// ABOUTME: Handles conflict resolution, queue management, and network detection

import { getDataService, type DataService } from './DataService'
import { getAuthService } from './AuthService'
import type { WardrobeItem, Outfit, ChallengeProgress, ModuleCompletion } from '@/types/models'

/**
 * Sync operation types
 */
type SyncOperation = 'create' | 'update' | 'delete'

/**
 * Sync queue item
 */
interface SyncQueueItem {
  id: string
  type: 'wardrobe' | 'outfit' | 'challenge' | 'module' | 'wearlog'
  operation: SyncOperation
  // biome-ignore lint/suspicious/noExplicitAny: sync queue data can be any shape
  data: any
  timestamp: Date
  retries: number
}

/**
 * Sync status
 */
export interface SyncStatus {
  online: boolean
  syncing: boolean
  lastSync?: Date
  pendingOperations: number
  failedOperations: number
}

/**
 * Sync conflict
 */
interface SyncConflict {
  type: 'wardrobe' | 'outfit' | 'challenge' | 'module' | 'wearlog'
  // biome-ignore lint/suspicious/noExplicitAny: conflict data can be any shape
  localData: any
  // biome-ignore lint/suspicious/noExplicitAny: conflict data can be any shape
  cloudData: any
  resolution?: 'local' | 'cloud' | 'merge'
}

/**
 * Offline-first sync service
 *
 * Provides bidirectional synchronization between local stores and cloud database
 * with conflict resolution and offline queue management
 */
export class SyncService {
  private queue: SyncQueueItem[] = []
  private syncing = false
  private online = typeof navigator !== 'undefined' ? navigator.onLine : true
  private lastSync?: Date
  private syncInterval?: ReturnType<typeof setInterval>
  private listeners: Array<(status: SyncStatus) => void> = []

  constructor() {
    this.loadQueue()
    this.setupNetworkListeners()
    this.startPeriodicSync()
  }

  /**
   * Get current sync status
   */
  getStatus(): SyncStatus {
    return {
      online: this.online,
      syncing: this.syncing,
      lastSync: this.lastSync,
      pendingOperations: this.queue.length,
      failedOperations: this.queue.filter((item) => item.retries > 0).length,
    }
  }

  /**
   * Subscribe to sync status changes
   */
  onStatusChange(callback: (status: SyncStatus) => void): () => void {
    this.listeners.push(callback)
    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback)
    }
  }

  /**
   * Queue a sync operation
   */
  queueOperation(
    type: 'wardrobe' | 'outfit' | 'challenge' | 'module' | 'wearlog',
    operation: SyncOperation,
    // biome-ignore lint/suspicious/noExplicitAny: sync queue data can be any shape
    data: any
  ): void {
    const item: SyncQueueItem = {
      id: crypto.randomUUID(),
      type,
      operation,
      data,
      timestamp: new Date(),
      retries: 0,
    }

    this.queue.push(item)
    this.saveQueue()
    this.notifyListeners()

    // Try to sync immediately if online
    if (this.online && !this.syncing) {
      this.sync().catch(console.error)
    }
  }

  /**
   * Perform synchronization
   */
  async sync(): Promise<void> {
    if (this.syncing || !this.online) {
      return
    }

    const authService = getAuthService()
    const user = await authService.getCurrentUser()

    if (!user) {
      // Can't sync without authentication
      return
    }

    this.syncing = true
    this.notifyListeners()

    try {
      // Process queue
      await this.processQueue(user.id)

      // Pull changes from cloud
      await this.pullFromCloud(user.id)

      this.lastSync = new Date()
    } catch (error) {
      console.error('Sync failed:', error)
    } finally {
      this.syncing = false
      this.notifyListeners()
    }
  }

  /**
   * Force full sync (pull everything from cloud)
   */
  async forcePull(): Promise<void> {
    const authService = getAuthService()
    const user = await authService.getCurrentUser()

    if (!user) {
      throw new Error('User must be authenticated to sync')
    }

    await this.pullFromCloud(user.id)
  }

  /**
   * Process sync queue
   */
  private async processQueue(userId: string): Promise<void> {
    const dataService = getDataService()
    const failedItems: SyncQueueItem[] = []

    for (const item of this.queue) {
      try {
        switch (item.type) {
          case 'wardrobe':
            await this.syncWardrobeItem(dataService, userId, item)
            break
          case 'outfit':
            await this.syncOutfit(dataService, userId, item)
            break
          case 'challenge':
            await this.syncChallengeProgress(dataService, userId, item)
            break
          case 'module':
            await this.syncModuleCompletion(dataService, userId, item)
            break
          case 'wearlog':
            if (item.operation === 'create') {
              await dataService.createWearLog(item.data)
            }
            break
        }
      } catch (error) {
        console.error(`Failed to sync ${item.type} ${item.operation}:`, error)
        item.retries++

        // Keep items that haven't exceeded retry limit
        if (item.retries < 3) {
          failedItems.push(item)
        }
      }
    }

    // Update queue with failed items
    this.queue = failedItems
    this.saveQueue()
  }

  /**
   * Sync wardrobe item
   */
  private async syncWardrobeItem(
    dataService: DataService,
    userId: string,
    item: SyncQueueItem
  ): Promise<void> {
    const data = item.data as WardrobeItem

    switch (item.operation) {
      case 'create':
        await dataService.createWardrobeItem({ ...data, userId })
        break
      case 'update':
        await dataService.updateWardrobeItem(data.id, data)
        break
      case 'delete':
        await dataService.deleteWardrobeItem(data.id)
        break
    }
  }

  /**
   * Sync outfit
   */
  private async syncOutfit(dataService: DataService, userId: string, item: SyncQueueItem): Promise<void> {
    const data = item.data as Outfit

    switch (item.operation) {
      case 'create':
        await dataService.createOutfit({ ...data, userId })
        break
      case 'update':
        await dataService.updateOutfit(data.id, data)
        break
      case 'delete':
        await dataService.deleteOutfit(data.id)
        break
    }
  }

  /**
   * Sync challenge progress
   */
  private async syncChallengeProgress(
    dataService: DataService,
    userId: string,
    item: SyncQueueItem
  ): Promise<void> {
    const data = item.data as ChallengeProgress
    await dataService.saveChallengeProgress({ ...data, userId })
  }

  /**
   * Sync module completion
   */
  private async syncModuleCompletion(
    dataService: DataService,
    userId: string,
    item: SyncQueueItem
  ): Promise<void> {
    const data = item.data as ModuleCompletion
    await dataService.saveModuleCompletion({ ...data, userId })
  }

  /**
   * Pull changes from cloud
   */
  private async pullFromCloud(userId: string): Promise<void> {
    const dataService = getDataService()

    // Pull wardrobe items
    const wardrobeItems = await dataService.getWardrobeItems(userId)
    if (wardrobeItems.length > 0) {
      // Trigger store updates via custom events
      window.dispatchEvent(
        new CustomEvent('cloud-sync:wardrobe', {
          detail: wardrobeItems,
        })
      )
    }

    // Pull outfits
    const outfits = await dataService.getOutfits(userId)
    if (outfits.length > 0) {
      window.dispatchEvent(
        new CustomEvent('cloud-sync:outfits', {
          detail: outfits,
        })
      )
    }

    // Pull challenge progress
    const challengeProgress = await dataService.getChallengeProgress(userId)
    if (challengeProgress.length > 0) {
      window.dispatchEvent(
        new CustomEvent('cloud-sync:challenges', {
          detail: challengeProgress,
        })
      )
    }

    // Pull module completions
    const moduleCompletions = await dataService.getModuleCompletions(userId)
    if (moduleCompletions.length > 0) {
      window.dispatchEvent(
        new CustomEvent('cloud-sync:modules', {
          detail: moduleCompletions,
        })
      )
    }
  }

  /**
   * Resolve sync conflict
   */
  resolveConflict(conflict: SyncConflict, resolution: 'local' | 'cloud' | 'merge'): void {
    // Conflict resolution logic
    switch (resolution) {
      case 'local':
        // Keep local data, queue update to cloud
        this.queueOperation(conflict.type, 'update', conflict.localData)
        break
      case 'cloud':
        // Accept cloud data, update local store
        window.dispatchEvent(
          new CustomEvent(`cloud-sync:${conflict.type}`, {
            detail: [conflict.cloudData],
          })
        )
        break
      case 'merge': {
        // Merge both versions (implement custom merge logic)
        const merged = this.mergeData(conflict.localData, conflict.cloudData)
        this.queueOperation(conflict.type, 'update', merged)
        break
      }
    }
  }

  /**
   * Merge conflicting data
   */
  // biome-ignore lint/suspicious/noExplicitAny: local and cloud data can be any shape
  private mergeData(local: any, cloud: any): any {
    // Simple merge: take most recent update
    const localTime = new Date(local.updatedAt || local.dateAdded || 0).getTime()
    const cloudTime = new Date(cloud.updatedAt || cloud.dateAdded || 0).getTime()

    return localTime > cloudTime ? local : cloud
  }

  /**
   * Setup network listeners
   */
  private setupNetworkListeners(): void {
    if (typeof window === 'undefined') return

    window.addEventListener('online', () => {
      this.online = true
      this.notifyListeners()
      this.sync().catch(console.error)
    })

    window.addEventListener('offline', () => {
      this.online = false
      this.notifyListeners()
    })
  }

  /**
   * Start periodic sync
   */
  private startPeriodicSync(): void {
    // Sync every 5 minutes when online
    this.syncInterval = setInterval(() => {
      if (this.online) {
        this.sync().catch(console.error)
      }
    }, 5 * 60 * 1000)
  }

  /**
   * Stop periodic sync
   */
  stopPeriodicSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval)
    }
  }

  /**
   * Save queue to localStorage
   */
  private saveQueue(): void {
    if (typeof localStorage === 'undefined') return
    try {
      localStorage.setItem('gentleman-sync-queue', JSON.stringify(this.queue))
    } catch {
      // Storage unavailable or full
    }
  }

  /**
   * Load queue from localStorage
   */
  private loadQueue(): void {
    if (typeof localStorage === 'undefined') return
    try {
      const saved = localStorage.getItem('gentleman-sync-queue')
      if (saved) {
        this.queue = JSON.parse(saved)
      }
    } catch {
      // Storage unavailable or corrupted
    }
  }

  /**
   * Notify status listeners
   */
  private notifyListeners(): void {
    const status = this.getStatus()
    for (const listener of this.listeners) {
      listener(status)
    }
  }
}

// Singleton instance
let syncServiceInstance: SyncService | null = null

/**
 * Get the sync service instance
 */
export function getSyncService(): SyncService {
  if (!syncServiceInstance) {
    syncServiceInstance = new SyncService()
  }
  return syncServiceInstance
}

/**
 * Initialize sync service (call from main.tsx)
 */
export function initializeSyncService(): void {
  getSyncService()
}
